import { isChord } from '../controls/keymap';
import { createElement, setText, getUiRoot, removeElement } from './dom';
import type { HelpModal } from './types';
import {
  MOVEMENT_ACTIONS,
  movementKeyLabel,
  movementPreferences,
} from '../controls/movementBindings';
import { MOVEMENT_LABELS } from './movementSettings';
import { setLabel } from './i18n';

interface KeyBinding {
  readonly keys: string;
  readonly description: string;
}

interface HelpSection {
  readonly title: string;
  readonly bindings: readonly KeyBinding[];
}

const HELP_SECTIONS: readonly HelpSection[] = [
  {
    title: 'Movement',
    bindings: [
      { keys: 'V', description: 'First-person / behind the visitor view' },
      { keys: 'Left mouse drag', description: 'Look around without turning the visitor' },
      { keys: 'Right mouse drag', description: 'Turn the visitor and camera' },
      { keys: 'Both mouse buttons', description: 'Move forward and steer' },
      { keys: 'Scroll', description: 'Zoom behind view / adjust fly speed' },
    ],
  },
  {
    title: 'View',
    bindings: [
      { keys: 'F', description: 'Toggle walk/fly mode' },
      { keys: 'B', description: 'Board the rowing boat / step ashore' },
      { keys: 'P', description: "Fly to painter's view" },
      { keys: 'O', description: 'Toggle painting overlay' },
      { keys: 'M', description: 'Toggle painting minimap (− / + to zoom)' },
      { keys: 'G', description: 'Cycle quality tier' },
      { keys: 'L', description: 'Show / hide coordinates (x, y, height, yaw, pitch)' },
      { keys: 'T', description: 'Choose a tour' },
      { keys: 'Space', description: 'Pause / Resume tour' },
    ],
  },
  {
    title: 'Interface',
    bindings: [
      { keys: 'N', description: 'Show / hide visitor names' },
      { keys: 'J', description: 'Choose a gesture' },
      { keys: 'K', description: 'Open the discovery book' },
      { keys: 'Shift+Enter', description: 'Talk to a townsperson' },
      { keys: 'Enter', description: 'Speak to nearby visitors' },
      { keys: 'H', description: 'Toggle this help' },
      { keys: 'Esc', description: 'Open city menu / Close modal' },
    ],
  },
];

const TITLE_ID = 'help-title';

function createHelpContent(multiplayer: boolean): HTMLElement {
  const content = createElement('div', 'help-content');

  const title = createElement('h2', 'help-title', content);
  title.id = TITLE_ID;
  setText(title, 'Amstelredam 1538');

  const touch = createElement('section', 'help-touch', content);
  setText(createElement('h3', 'help-section-title', touch), 'Touch controls');
  setText(
    createElement('p', 'help-desc', touch),
    'Hold the arrows to move. Swipe the scene to look around. Run changes your pace. Menu opens settings and tours.'
  );

  for (const section of HELP_SECTIONS) {
    const sectionEl = createElement('div', 'help-section', content);

    const sectionTitle = createElement('h3', 'help-section-title', sectionEl);
    setText(sectionTitle, section.title);

    const keysContainer = createElement('div', 'help-keys', sectionEl);

    const movement = movementPreferences();
    const bindings =
      section.title === 'Movement'
        ? [
            ...section.bindings,
            ...MOVEMENT_ACTIONS.map((action) => ({
              keys: movementKeyLabel(movement.bindings[action]),
              description: MOVEMENT_LABELS[action],
            })),
            {
              keys: `${movementKeyLabel(movement.bindings.turnLeft)} / ${movementKeyLabel(movement.bindings.turnRight)}`,
              description: 'Turn, or strafe while holding right mouse',
            },
            {
              keys: `${movementKeyLabel(movement.bindings.forward)} / ${movementKeyLabel(movement.bindings.backward)}, ${movementKeyLabel(movement.bindings.turnLeft)} / ${movementKeyLabel(movement.bindings.turnRight)}`,
              description: 'In the boat: row ahead / astern, turn the bow',
            },
          ]
        : section.bindings;
    for (const binding of bindings) {
      if (!multiplayer && binding.description === 'Speak to nearby visitors') continue;
      if (multiplayer && ['F', 'P', 'O', 'T'].includes(binding.keys)) continue;
      if (multiplayer && ['Ascend (fly mode)', 'Descend (fly mode)'].includes(binding.description))
        continue;
      const row = createElement('div', 'help-key-row', keysContainer);

      const keyEl = createElement('span', 'help-key', row);
      setText(keyEl, binding.keys);

      const descEl = createElement('span', 'help-desc', row);
      setText(
        descEl,
        multiplayer && binding.description === 'Run / Boost'
          ? 'Run / Pull hard (boat)'
          : multiplayer && binding.keys === 'Scroll'
            ? 'Zoom behind view'
            : binding.description
      );
    }
  }

  return content;
}

/* The modal never closes itself: every dismiss intent (H, Esc, a click outside the content) goes
   to the owner, which also re-enables input. Keys are taken in the capture phase and stopped so
   the input adapter never sees the key that closed the help, whatever the listener order. */
export function createHelpModal(onDismiss: () => void, multiplayer = () => false): HelpModal {
  let overlay: HTMLElement | null = null;
  let isOpen = false;

  function handleOverlayClick(e: MouseEvent): void {
    if (e.target === overlay) {
      onDismiss();
    }
  }

  function handleKeyDown(e: KeyboardEvent): void {
    if (e.code === 'Escape' || (e.code === 'KeyH' && !isChord(e))) {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    }
  }

  function open(): void {
    if (isOpen) return;

    overlay = createElement('div', 'modal-overlay');
    const modalContent = createElement('div', 'modal-content', overlay);
    modalContent.setAttribute('role', 'dialog');
    modalContent.setAttribute('aria-modal', 'true');
    modalContent.setAttribute('aria-labelledby', TITLE_ID);
    const close = createElement('button', 'help-close', modalContent);
    close.type = 'button';
    setText(close, 'Close');
    setLabel(close, 'aria-label', 'Close help');
    close.addEventListener('click', onDismiss);
    modalContent.appendChild(createHelpContent(multiplayer()));

    overlay.addEventListener('click', handleOverlayClick);
    window.addEventListener('keydown', handleKeyDown, { capture: true });

    getUiRoot().appendChild(overlay);
    overlay.style.display = 'flex';
    isOpen = true;
  }

  function close(): void {
    if (!isOpen || !overlay) return;

    overlay.removeEventListener('click', handleOverlayClick);
    window.removeEventListener('keydown', handleKeyDown, { capture: true });

    removeElement(overlay);
    overlay = null;
    isOpen = false;
  }

  return {
    open,
    close,
    get isOpen(): boolean {
      return isOpen;
    },
  };
}
